import React from 'react';
import { useConfig } from '../../config/ConfigContext';

/**
 * Right-side action buttons for shorts viewer
 * Includes: Speaker, Like, Share, YouTube Subscribe, Lecture Link
 * Modern SVG icons with better mobile responsiveness
 */
const ShortsActions = ({
  speakerName,
  likeCount,
  hasLiked,
  onSpeakerClick,
  onLikeClick,
  onShareClick,
  onYouTubeClick,
  onLectureClick,
  hasLectureLink,
  showYouTubeButton = true,
  shareEnabled = true
}) => {
  const { theme } = useConfig();
  
  // Get speaker initials from name
  const getInitials = (name) => {
    if (!name) return 'SP';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Format like count for display
  const formatLikeCount = (count) => {
    if (!count || count === 0) return '0';
    if (count < 1000) return count.toString();
    if (count < 1000000) return (count / 1000).toFixed(1) + 'k';
    return (count / 1000000).toFixed(1) + 'M';
  };

  return (
    <div className="shorts-actions">
      {/* YouTube Subscribe Button - Moved to top (hidden for Lecture Clips) */}
      {showYouTubeButton && (
      <div className="action-item youtube-action">
        <button
          className="youtube-subscribe-button"
          onClick={onYouTubeClick}
          aria-label="Subscribe to YouTube Channel"
        >
          <span className="youtube-text">YT</span>
        </button>
        <span className="youtube-label">Subscribe</span>
      </div>
      )}

      {/* Share Button — hidden when center disables clip sharing */}
      {shareEnabled && (
      <div className="action-item share-action">
        <button
          className="share-button"
          onClick={onShareClick}
          aria-label="Share video"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8"/>
            <polyline points="16 6 12 2 8 6"/>
            <line x1="12" y1="2" x2="12" y2="15"/>
          </svg>
        </button>
        <span className="share-label">Share</span>
      </div>
      )}

      {/* Like Button - Middle */}
      <div className="action-item like-action">
        <button 
          className={`like-button ${hasLiked ? 'liked' : ''}`}
          onClick={onLikeClick}
          aria-label={hasLiked ? 'Unlike' : 'Like'}
        >
          {hasLiked ? (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="#ff4458" stroke="none">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
            </svg>
          ) : (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          )}
        </button>
        <span className="like-count">{formatLikeCount(likeCount)}</span>
      </div>

      {/* Smart Lecture Notes Button */}
      <div className="action-item lecture-action">
        <button 
          className={`lecture-button ${hasLectureLink ? 'has-lecture' : 'no-lecture'}`}
          onClick={hasLectureLink ? onLectureClick : () => {}}
          aria-label={hasLectureLink ? "Go to lecture notes" : "No lecture notes available"}
          disabled={!hasLectureLink}
          style={{
            background: 'none',
            border: 'none',
            cursor: hasLectureLink ? 'pointer' : 'not-allowed',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'transform 0.2s ease',
            opacity: hasLectureLink ? 1 : 0.5
          }}
        >
          {hasLectureLink ? (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
              <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
            </svg>
          ) : (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="white" opacity="0.5">
              <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
              <line x1="5" y1="5" x2="19" y2="19" stroke="white" strokeWidth="2"/>
            </svg>
          )}
        </button>
        <span className="lecture-label">
          {hasLectureLink ? 'Lecture Notes' : 'No Notes'}
        </span>
      </div>

      {/* Speaker Button - Moved to bottom */}
      <div className="action-item speaker-action">
        <button 
          className={`speaker-button ${hasLectureLink ? 'has-lecture' : 'no-lecture'}`}
          onClick={hasLectureLink ? onSpeakerClick : () => {}}
          aria-label={hasLectureLink ? `View all lectures by ${speakerName}` : "Speaker profile unavailable - no lecture notes"}
          disabled={!hasLectureLink}
          style={{
            background: 'none',
            border: 'none',
            cursor: hasLectureLink ? 'pointer' : 'not-allowed',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'transform 0.2s ease',
            opacity: hasLectureLink ? 1 : 0.5
          }}
        >
          {hasLectureLink ? (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
          ) : (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="white" opacity="0.5">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
              <line x1="5" y1="5" x2="19" y2="19" stroke="white" strokeWidth="2"/>
            </svg>
          )}
        </button>
        <span className={`speaker-name ${hasLectureLink ? '' : 'disabled'}`} style={{
          opacity: hasLectureLink ? 1 : 0.5
        }}>{speakerName || 'Speaker'}</span>
      </div>
    </div>
  );
};

export default ShortsActions;